import React, { useState, useRef } from "react";
import { API_TXALERT } from "../../config/config";
import { Modal, Button, Upload, Tooltip, Tag, message } from "antd";
import ToBase64 from "../../helper/ToBase64";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import { UploadOutlined, QuestionCircleOutlined, PaperClipOutlined, InboxOutlined } from "@ant-design/icons";
import CreditDocumentTooltip from "../../helper/CreditDocumentTooltip";
import formatFileSize from "../../helper/FormatFileSize";
import { normalizeFilename, showDuplicateError, resultMessage } from "../../helper/CreditDocumentDuplicate";
import UPLOAD_SUCCESS_DURATION_MS from "../../helper/UploadSuccessDuration";
import { mismatchedAlertId, alertIdMismatchMessage, showAlertIdMismatchWarning } from "../../helper/CreditDocumentAlertId";

// Base64 inflates payload by ~33%, so 5MB raw ≈ 6.7MB base64 - stays under the server's 8MB post_max_size limit.
const MAX_TOTAL_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_TOTAL_SIZE_MB = MAX_TOTAL_SIZE_BYTES / (1024 * 1024);
// Fixed width for the Remove link so the total line stays aligned with the file sizes in every language.
const REMOVE_COLUMN_WIDTH = 64;

const AlertCreditDocumentUpload = (props) => {
    const { t, alertid, onUploadSuccess, forceOpen, onClose, existingFilenames = [] } = props;
    const [files, setFiles] = useState([]);
    // Per-file rejection reason from the last upload, keyed by uid.
    const [fileErrors, setFileErrors] = useState({});
    const [isModalVisible, setIsModalVisible] = useState(!!forceOpen);
    const [loading, setLoading] = useState(false);
    const totalSizeRef = useRef(0);
    // Mirrors the selected filenames so a multi-file drop sees files added earlier in the same drop.
    const namesRef = useRef(new Set());

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setFiles([]);
        setFileErrors({});
        totalSizeRef.current = 0;
        namesRef.current = new Set();
        setLoading(false);
        setIsModalVisible(false);
        if (onClose) onClose();
    };

    const getMimeType = (file) => {
        return file.type || "application/octet-stream";
    };

    const handleUpload = async () => {
        if (files.length === 0) return;
        setLoading(true);

        try {
            const documents = await Promise.all(
                files.map(async (file) => {
                    const base64Full = await ToBase64(file);
                    const content = base64Full.replace(/^data:[^;]+;base64,/, "");
                    return {
                        filename: file.name,
                        filetype: getMimeType(file),
                        content: content,
                    };
                })
            );

            const response = await axios.post(
                API_TXALERT + "/credit/documents/upload",
                { alert_id: alertid, documents: documents },
                { headers: { Authorization: "Bearer " + Cookies.get("access_token") } }
            );

            const result = response.data;
            const failed = (result.results || []).filter((r) => !r.status);

            if (result.status === "ok" && result.uploaded > 0 && failed.length === 0) {
                handleCancel();
                Swal.fire({
                    icon: "success",
                    title: t("contents.alerts.document_upload_successful") || "Documents uploaded successfully",
                    text: `${result.uploaded} file(s) uploaded`,
                    showConfirmButton: false,
                    timer: UPLOAD_SUCCESS_DURATION_MS,
                    timerProgressBar: true,
                });
                if (onUploadSuccess) onUploadSuccess();
            } else if (result.status === "error") {
                message.error(result.message);
                setLoading(false);
            } else if (failed.length > 0) {
                // Keep only the rejected files, each showing why, so the user can see what was not attached.
                const reasonByName = new Map(failed.map((r) => [r.filename, resultMessage(t, r)]));
                const remaining = files.filter((f) => reasonByName.has(f.name));
                setFiles(remaining);
                setFileErrors(Object.fromEntries(remaining.map((f) => [f.uid, reasonByName.get(f.name)])));
                totalSizeRef.current = remaining.reduce((sum, f) => sum + f.size, 0);
                namesRef.current = new Set(remaining.map((f) => normalizeFilename(f.name)));
                setLoading(false);
                // Always summarize: the per-file tags can't explain anything if no filename matched a result.
                if (result.uploaded > 0) {
                    message.warning(
                        t("contents.alerts.document_upload_partial", { uploaded: result.uploaded, failed: failed.length }) ||
                            `${result.uploaded} file(s) uploaded, ${failed.length} failed.`,
                        5
                    );
                    if (onUploadSuccess) onUploadSuccess();
                } else {
                    message.error(t("contents.alerts.document_upload_none") || "No files were uploaded.", 5);
                }
            } else {
                message.error(t("contents.alerts.document_upload_none") || "No files were uploaded.");
                setLoading(false);
            }
        } catch (error) {
            const msg = error?.response?.data?.message || error?.response?.data?.msg || error.message || "Upload failed";
            message.error(msg);
            setLoading(false);
        }
    };

    const uploadProps = {
        accept: ".pdf, .doc, .docx, .png, .jpg, .jpeg",
        multiple: true,
        showUploadList: false, // file list rendered manually below to keep the info icon row height stable
        beforeUpload: (file) => {
            const name = normalizeFilename(file.name);
            if (namesRef.current.has(name) || existingFilenames.some((n) => normalizeFilename(n) === name)) {
                showDuplicateError(t, file.name);
                return false;
            }
            const newTotal = totalSizeRef.current + file.size;
            if (newTotal > MAX_TOTAL_SIZE_BYTES) {
                const usedMB = (totalSizeRef.current / (1024 * 1024)).toFixed(2);
                message.error(
                    `Total file size would exceed ${MAX_TOTAL_SIZE_MB}MB (currently ${usedMB}MB used). Please remove some files before adding more.`,
                    5
                );
                return false;
            }
            totalSizeRef.current = newTotal;
            namesRef.current.add(name);
            const fileAlertId = mismatchedAlertId(file.name, alertid);
            if (fileAlertId) showAlertIdMismatchWarning(t, file.name, fileAlertId);
            setFiles((prev) => [...prev, file]);
            return false;
        },
    };

    const totalSizeBytes = files.reduce((sum, f) => sum + f.size, 0);
    const hasFileErrors = files.some((f) => fileErrors[f.uid]);

    const handleRemoveFile = (uid) => {
        setFiles((prev) => {
            const next = prev.filter((f) => f.uid !== uid);
            totalSizeRef.current = next.reduce((sum, f) => sum + f.size, 0);
            namesRef.current = new Set(next.map((f) => normalizeFilename(f.name)));
            return next;
        });
        setFileErrors((prev) => {
            const { [uid]: _removed, ...rest } = prev;
            return rest;
        });
    };

    return (
        <>
            {/* Suppress trigger button when modal is opened programmatically via forceOpen */}
            {!forceOpen && (
                <Button icon={<UploadOutlined />} onClick={showModal} style={{ marginBottom: "1rem" }}>
                    {t("contents.alerts.upload_credit_documents") || "Upload Credit Documents"}
                </Button>
            )}
            <Modal
                title={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {`${t("contents.alerts.upload_credit_documents") || "Upload Credit Documents"} - Alert #${alertid}`}
                        <Tooltip title={<CreditDocumentTooltip t={t} />} overlayStyle={{ maxWidth: 400 }}>
                            <QuestionCircleOutlined style={{ color: "#999", cursor: "pointer" }} />
                        </Tooltip>
                    </span>
                }
                width={600}
                visible={isModalVisible}
                onOk={handleUpload}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        {t("contents.chargebacks.form.cancel")}
                    </Button>,
                    <Button key="submit" type="primary" loading={loading} disabled={files.length === 0 || hasFileErrors || loading} onClick={handleUpload}>
                        {loading ? (t("contents.chargebacks.form.uploading") || "Uploading...") : (t("contents.chargebacks.form.upload") || "Upload")}
                    </Button>,
                ]}
            >
                <Upload.Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        {t("contents.credit_report.batch_upload_drag") || "Click or drag files to this area to upload"}
                    </p>
                    <p className="ant-upload-hint">
                        {t("contents.alerts.upload_documents_hint") || `Supported file types: PDF, DOC, DOCX, PNG, JPG, JPEG. Maximum combined file size: ${MAX_TOTAL_SIZE_MB} MB.`}
                    </p>
                </Upload.Dragger>
                {files.map((file, index) => (
                    <div
                        key={file.uid}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "4px 0",
                            gap: 6,
                            marginTop: index === 0 ? 8 : undefined,
                            borderTop: index > 0 ? "1px solid #f0f0f0" : undefined,
                        }}
                    >
                        <PaperClipOutlined style={{ color: "#999", flexShrink: 0 }} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14 }}>
                            {file.name}
                        </span>
                        {fileErrors[file.uid] && (
                            <Tag color="error" style={{ marginRight: 0, flexShrink: 0 }}>{fileErrors[file.uid]}</Tag>
                        )}
                        {!fileErrors[file.uid] && mismatchedAlertId(file.name, alertid) && (
                            <Tag color="warning" style={{ marginRight: 0, flexShrink: 0 }}>
                                {alertIdMismatchMessage(t, mismatchedAlertId(file.name, alertid))}
                            </Tag>
                        )}
                        <span style={{ color: "#999", fontSize: 12, whiteSpace: "nowrap", flexShrink: 0, marginLeft: "auto" }}>
                            ({formatFileSize(file.size)})
                        </span>
                        <Button
                            type="link"
                            danger
                            size="small"
                            style={{ width: REMOVE_COLUMN_WIDTH, padding: 0, textAlign: "right", flexShrink: 0 }}
                            onClick={() => handleRemoveFile(file.uid)}
                        >
                            {t("tables.actions.remove") || "Remove"}
                        </Button>
                    </div>
                ))}
                {files.length > 0 && (
                    <div style={{ display: "flex", justifyContent: "flex-end", padding: `4px ${REMOVE_COLUMN_WIDTH + 6}px 0 0`, borderTop: "1px solid #f0f0f0", fontSize: 12 }}>
                        <span style={{ color: totalSizeBytes > MAX_TOTAL_SIZE_BYTES ? "#ff4d4f" : "#52c41a", fontWeight: 500 }}>
                            {t("contents.alerts.total_size") || "Total:"} {formatFileSize(totalSizeBytes)} / {MAX_TOTAL_SIZE_MB}MB
                        </span>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default AlertCreditDocumentUpload;
